import React from 'react';
import { Shield, FileText, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const OverviewSection = () => {
  const features = [
    {
      icon: Shield,
      title: 'Complete Brand Protection',
      description: 'Secure nationwide exclusive rights to your trademark and defend against infringement'
    },
    {
      icon: FileText,
      title: 'Professional USPTO Filing',
      description: 'Expert preparation and submission of all required documentation to the USPTO'
    },
    {
      icon: Clock,
      title: 'Ongoing Support',
      description: 'Regular updates, monitoring services, and assistance throughout the entire process'
    },
    {
      icon: CheckCircle,
      title: 'High Success Rate',
      description: '95% approval rate with comprehensive search and meticulous application preparation'
    }
  ];

  return (
    <section id="overview" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-4">
            Trademark Registration Made Simple
          </h2>
          <p className="text-lg text-[#8D6E63] max-w-4xl mx-auto leading-relaxed mb-6">
            Trademark registration is the legal process of securing exclusive rights to use a specific mark—whether it's a name, logo, slogan, or symbol—to identify your goods or services. A federally registered trademark provides nationwide protection, legal presumption of ownership, and the exclusive right to use the ® symbol.
          </p>
          <p className="text-lg text-[#8D6E63] max-w-4xl mx-auto leading-relaxed">
            In today's competitive business environment, trademark registration is not optional—it's essential for protecting your brand identity, preventing customer confusion, and building long-term business value. Without federal trademark protection, you risk losing your brand to competitors, facing costly legal disputes, and missing opportunities for business growth through licensing and franchising.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="border-2 border-[#D7CCC8] hover:border-[#8D6E63] transition-all duration-300 hover:shadow-lg bg-[#EFEBE9]"
              >
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-[#3E2723] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#3E2723] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-[#8D6E63] text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="bg-[#EFEBE9] rounded-lg p-8 border-2 border-[#D7CCC8]">
          <h3 className="text-2xl font-bold text-[#3E2723] mb-6 text-center">
            Understanding USPTO Trademark Registration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-[#8D6E63] mb-6">
            <div>
              <h4 className="font-bold text-[#3E2723] mb-3 text-lg">What is the USPTO?</h4>
              <p className="leading-relaxed mb-4">
                The United States Patent and Trademark Office (USPTO) is the federal agency responsible for granting U.S. patents and registering trademarks. Established in 1790, the USPTO examines trademark applications to ensure they meet legal requirements and don't conflict with existing registered marks.
              </p>
              <p className="leading-relaxed">
                USPTO trademark registration provides nationwide protection and is recognized in federal courts across all 50 states. Unlike state registrations or common law rights, federal registration offers superior legal protections, including the ability to bring infringement lawsuits in federal court and prevent importation of infringing goods.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-[#3E2723] mb-3 text-lg">Why Federal Registration?</h4>
              <p className="leading-relaxed mb-4">
                While common law trademark rights exist through actual use in commerce, these rights are limited to the specific geographic areas where you do business. Federal registration provides nationwide protection from day one, even in markets you haven't yet entered, giving you priority over later users across the entire United States.
              </p>
              <p className="leading-relaxed">
                Federal registration also provides powerful legal advantages: legal presumption of ownership, exclusive nationwide rights, the ability to sue in federal court, eligibility for statutory damages and attorney's fees, use of the ® registration symbol, and the right to record your registration with U.S. Customs to prevent importation of counterfeit goods. These benefits make federal registration far superior to state or common law protection.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 border border-[#D7CCC8]">
            <h4 className="font-bold text-[#3E2723] mb-3 text-lg">The Value of Professional Assistance</h4>
            <p className="text-[#8D6E63] leading-relaxed">
              While it's technically possible to file a trademark application yourself, the USPTO reports that applications filed without professional assistance have significantly higher rates of refusal and abandonment. Trademark law is complex, involving nuanced assessments of likelihood of confusion, proper identification of goods and services, and strategic responses to office actions. Our expertise ensures your application is prepared correctly the first time, maximizing your chances of approval while minimizing delays and complications. We've successfully navigated thousands of trademark applications through the USPTO examination process, handling everything from straightforward registrations to complex office action responses and opposition proceedings.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
